<template>
  <section class="section container">
    <div class="container mt-0 mb-6">
      <h1 class="title is-4 mb-3 is-pulled-right">
        {{ $t("credit analysis settings") }}
      </h1>
    </div>

    <!-- SIRC Settings -->
    <div class="mt-5">
      <h1 class="title is-5">{{ $t("sirc settings") }}</h1>

      <div class="columns">
        <div class="column is-4">
          <!-- Format change date -->
          <div class="field">
            <label class="label">{{ $t("format change date") }}</label>
            <input
              type="date"
              class="input"
              v-model="meta.sirc_format_change_date"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Distribution Settings -->
    <div class="mt-5">
      <h1 class="title is-5">{{ $t("distribution settings") }}</h1>

      <div class="notification is-info is-dark">
        {{
          $t("incoming requests will be assigned to the selected users equally")
        }}
      </div>

      <div class="columns">
        <div class="column">
          <!-- Users to distribute -->
          <generic-dropdown
            :title="$t('users to distribute')"
            :placeholder="$t('select one')"
            :is_multiple="true"
            url="/access/user/catalog"
            name_field="username"
            value_field="user_id"
            @selected="distribution_users_selected"
            ref="distribution_users_dropdown"
          />
        </div>
      </div>
    </div>

    <!-- Reject reasons -->
    <h1 class="title is-5 mt-5">{{ $t("reject reasons") }}</h1>
    <deny-reason-datatable ref="deny_reason_datatable" />

    <!-- External database -->
    <div class="box">
      <div class="title is-4">
        {{ $t("external database configuration") }}
      </div>
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("host") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.external_db_host"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-server"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("port") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.external_db_port"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-plug"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("database") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.external_db_name"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-database"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("username") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.external_db_username"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-user"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("password") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="password"
                v-model="meta.external_db_password"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-lock"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("test") }}</label>
            <div class="control">
              <button
                class="button is-rounded is-primary"
                @click="test_external_db_connection"
              >
                <span class="icon"><i class="fa fa-check"></i></span>
                <span>{{ $t("test connection") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Settings -->
    <div class="mt-6">
      <h1 class="title is-5">{{ $t("notification settings") }}</h1>

      <div class="columns">
        <div class="column">
          <div class="field">
            <input
              class="is-checkradio"
              type="checkbox"
              id="chk_notify_request"
              v-model="meta.notify_on_request_creation"
            />
            <label for="chk_notify_request">{{
              $t("notify on request creation")
            }}</label>
          </div>

          <!-- Notify on evaluation completion -->
        </div>
        <div class="column">
          <div
            class="field"
            :class="{ 'is-hidden': !meta.notify_on_request_creation }"
          >
            <generic-dropdown
              :title="$t('create request notification template')"
              :placeholder="$t('select one')"
              :is_multiple="false"
              url="/outbox/template/catalog"
              name_field="name"
              value_field="template_id"
              @selected="creation_template_selected"
              ref="notify_creation_dropdown"
            />
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <div class="field">
            <input
              class="is-checkradio"
              type="checkbox"
              id="chk_notify_evaluation"
              v-model="meta.notify_on_evaluation_completion"
            />
            <label for="chk_notify_evaluation">{{
              $t("notify on evaluation completion")
            }}</label>
          </div>
        </div>
        <div class="column">
          <div
            class="field"
            :class="{ 'is-hidden': !meta.notify_on_evaluation_completion }"
          >
            <generic-dropdown
              :title="$t('select evaluation notification template')"
              :placeholder="$t('select one')"
              :is_multiple="false"
              url="/outbox/template/catalog"
              name_field="name"
              value_field="template_id"
              @selected="evaluation_template_selected"
              ref="evaluation_template_dropdown"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- System Configuration -->
    <div class="mt-5">
      <h1 class="title is-5">{{ $t("system configuration") }}</h1>

      <!-- APY function -->
      <div class="field">
        <label class="label">{{
          $t("apy endpoint for sirc pdf reading")
        }}</label>
        <input
          type="text"
          class="input"
          v-model="meta.apy_endpoint_sirc_pdf_reading"
        />
      </div>

      <div class="columns">
        <div class="column">
          <div class="field">
            <input
              class="is-checkradio"
              type="checkbox"
              id="chk_save_sirc_pdf"
              v-model="meta.save_sirc_pdf"
            />
            <label for="chk_save_sirc_pdf">{{ $t("save sirc pdf") }}</label>
          </div>
        </div>
        <div class="column">
          <div class="field" :class="{ 'is-hidden': !meta.save_sirc_pdf }">
            <generic-dropdown
              :title="$t('select drive to save')"
              :placeholder="$t('select one')"
              :is_multiple="false"
              url="/drive/catalog"
              name_field="name"
              value_field="drive_id"
              @selected="drive_selected"
              ref="drive_dropdown"
            />
          </div>
        </div>
      </div>

      <!-- Default request status
      <div class="field">
        <label class="label">{{ $t("default request status") }}</label>
        <div class="select is-fullwidth">
          <select v-model="meta.default_request_status">
            <option value="pending">{{ $t("pending") }}</option>
            <option value="in_progress">{{ $t("in progress") }}</option>
          </select>
        </div>
      </div>

      <div class="field">
        <label class="label">{{ $t("archive evaluations after days") }}</label>
        <input
          type="number"
          class="input"
          v-model="meta.archive_after_days"
          min="30"
          max="3650"
        />
        <p class="help">
          {{ $t("days before archiving completed evaluations") }}
        </p>
      </div>

      <div class="field">
        <input
          class="is-checkradio"
          type="checkbox"
          id="chk_audit_trail"
          v-model="meta.enable_audit_trail"
        />
        <label for="chk_audit_trail">{{ $t("enable audit trail") }}</label>
      </div> -->
    </div>

    <div class="columns">
      <div class="column">
        <div class="field">
          <input
            class="is-checkradio"
            type="checkbox"
            id="chk_integrate_debt_level"
            v-model="meta.integrate_debt_level"
          />
          <label for="chk_integrate_debt_level">{{
            $t("integrate debt level module")
          }}</label>
        </div>
      </div>
      <div class="column"></div>
    </div>

    <!-- Save Button -->
    <div class="container mt-6">
      <div class="field mt-5">
        <div class="control">
          <button
            class="button is-rounded is-primary"
            @click="save"
            :disabled="is_loading"
          >
            <span class="icon"><i class="fa fa-save"></i></span>
            <span>{{ $t("save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import DenyReasonDatatable from "./deny_reason_datatable/Index.vue";

import {
  save_configuration,
  get_configuration,
  success_message,
  error_message,
  post,
} from "@/utils";

export default {
  name: "CreditAnalysisSettings",
  components: {
    GenericDropdown,
    DenyReasonDatatable,
  },
  data() {
    return {
      is_loading: false,
      meta: {
        // Notification Settings
        notify_on_request_creation: false,
        notify_on_evaluation_completion: false,

        // System Configuration
        default_request_status: "pending",
        archive_after_days: 365,
        enable_audit_trail: true,
      },
    };
  },
  methods: {
    save() {
      let v = this;
      v.is_loading = true;

      save_configuration(this, v.meta)
        .then(() => {
          success_message(v.$t("configuration saved"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          v.is_loading = false;
        });
    },
    creation_template_selected(template) {
      if (template) {
        this.meta.creation_notification_template = template.template_id;
      }
    },
    evaluation_template_selected(template) {
      if (template) {
        this.meta.evaluation_notification_template = template.template_id;
      }
    },
    drive_selected(drive) {
      if (drive) {
        this.meta.sirc_drive_id = drive.drive_id;
      }
    },
    distribution_users_selected(users) {
      if (users) {
        if (Array.isArray(users))
          this.meta.distribution_user_ids = users.map((u) => u.user_id);
        else this.meta.distribution_user_ids = [users.user_id];
      }
    },
    test_external_db_connection() {
      let v = this;
      v.$root.loader(true);

      post("/credit-analysis/request/test-external-db-connection", {
        host: v.meta.external_db_host,
        port: v.meta.external_db_port,
        database: v.meta.external_db_name,
        username: v.meta.external_db_username,
        password: v.meta.external_db_password,
      })
        .then(() => {
          success_message(v.$t("connection successful"));
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
  },
  created() {
    let v = this;
    v.is_loading = true;

    get_configuration(this)
      .then((conf) => {
        if (conf) {
          // Merge with defaults to ensure all properties exist
          v.meta = { ...v.meta, ...conf };

          if (conf.creation_notification_template)
            v.$refs.notify_creation_dropdown.set_value(
              conf.creation_notification_template
            );

          if (conf.evaluation_notification_template)
            v.$refs.evaluation_template_dropdown.set_value(
              conf.evaluation_notification_template
            );

          if (conf.sirc_drive_id)
            v.$refs.drive_dropdown.set_value(conf.sirc_drive_id);

          if (conf.distribution_user_ids)
            v.$refs.distribution_users_dropdown.set_value(
              conf.distribution_user_ids
            );
        }
      })
      .catch((error) => {
        error_message(error);
      })
      .finally(() => {
        v.is_loading = false;
      });
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 1.5rem;
}

.title.is-5 {
  border-bottom: 2px solid #dbdbdb;
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
}

.help {
  font-size: 0.875rem;
  color: #888;
}
</style>
